"use client";

import { useEffect, useSyncExternalStore } from "react";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { lazyComponent } from "@/components/ui/lazy-component";
import { navigateToDocument } from "@/lib/document-navigation";
import { SIGN_IN_PARAM } from "@/lib/auth/oauth-providers";
import { hasEmailedSignInMarker } from "@shared/auth/emailed-sign-in-marker";
import { hasSessionHintCookie } from "@/lib/auth/session-hint-cookie";
import {
  closeSignInModal,
  getClosedSignInModalState,
  getSignInModalState,
  openSignInModal,
  readSignInParam,
  subscribeSignInModal,
} from "@/lib/auth/sign-in-modal-store";

const SignInModal = lazyComponent(
  () => import("./sign-in-modal").then((module) => module.SignInModal),
  { loading: null, ssr: false },
);

function signInOnHostedPage(): void {
  closeSignInModal();
  const returnTo = `${window.location.pathname}${window.location.search}`;
  navigateToDocument(`/sign-in?${new URLSearchParams({ returnTo }).toString()}`);
}

function openFromReturnParam(): void {
  const url = new URL(window.location.href);
  const requested = readSignInParam(url.searchParams.get(SIGN_IN_PARAM));
  if (!requested) return;
  url.searchParams.delete(SIGN_IN_PARAM);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  openSignInModal(requested);
}

async function openFromEmailedLink(): Promise<void> {
  if (!hasEmailedSignInMarker(window.location.hash)) return;
  const { clearEmailedSignIn, readEmailedSignIn } = await import("@/lib/auth/emailed-sign-in");
  const emailed = readEmailedSignIn();
  if (!emailed) return;
  if (hasSessionHintCookie()) {
    clearEmailedSignIn();
    return;
  }
  if (emailed.flow !== "modal") return;
  clearEmailedSignIn();
  openSignInModal({ view: "code", email: emailed.email, code: emailed.code });
}

function openFromAddress(): void {
  openFromReturnParam();
  void openFromEmailedLink();
}

export function SignInModalHost() {
  const state = useSyncExternalStore(
    subscribeSignInModal,
    getSignInModalState,
    getClosedSignInModalState,
  );

  useEffect(openFromAddress, []);

  if (!state.open) return null;
  return (
    <ErrorBoundary label="sign-in-modal" fallback={null} onError={signInOnHostedPage}>
      <SignInModal
        view={state.view}
        notice={state.notice}
        email={state.email}
        code={state.code}
        onClose={closeSignInModal}
      />
    </ErrorBoundary>
  );
}
